import { Button } from "@/components/new/button";
import { PRODUCT_VOTES_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ModalType } from "@/types/modal-type";
import { TProductVotes } from "@/shared/service/votes/productv2-vote";
import { TUserVoteType } from "@/types/votes";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Star } from "lucide-react";
import Image from "next/image";
import toast from "react-hot-toast";

interface UpVoteButtonProps {
	id: string;
	name?: string;
	slug?: string;
	upVotes: number;
	rating: string;
	reviewsCount: number;
	isUpvoted?: boolean;
	isPremium?: boolean;
	keyToInvalidate: string[];
}

const UpVoteButton = ({
	id,
	name,
	slug,
	upVotes,
	rating,
	reviewsCount,
	isPremium,
	keyToInvalidate,
	isUpvoted: isUpvotedPreviously = false,
}: UpVoteButtonProps) => {
	const { isLoggedIn } = useAuth();
	const queryClient = useQueryClient();
	const { openModal } = useModal();

	const vote = async (id: string, type: TUserVoteType) => {
		const url = new URL(PRODUCT_VOTES_API);
		url.searchParams.set("productId", id);
		url.searchParams.set("type", type);

		const response = await fetch(url, {
			method: "PUT",
			credentials: "include",
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw new Error(errorData.message || "Failed to vote");
		}

		return await response.json();
	};

	const productsMutation = useMutation<
		// TODO: Add type enforcement
		// Because there is no type-enforcement here, any addition to the upvote-api will be lost
		any,
		Error,
		{ id: string; type: TUserVoteType },
		{ previousData: TProductVotes }
	>({
		mutationFn: ({ id, type }) => vote(id, type),
		onMutate: async ({ type }) => {
			await queryClient.cancelQueries({ queryKey: keyToInvalidate });
			const previousData = queryClient.getQueryData(keyToInvalidate) as TProductVotes;

			queryClient.setQueryData(keyToInvalidate, (old?: TProductVotes) => {
				if (!old) return undefined;
				return {
					data: {
						...old,
						votesCount: type === "upvote" ? old.data.votesCount + 1 : old.data.votesCount - 1,
						score: type === "upvote" ? old.data.score + 1 : old.data.score - 1,
						isUpvotedByUser: type === "upvote",
						usersWhoVoted:
							type === "upvote"
								? [
										...(old.data.usersWhoVoted || []),
										{
											id: "temp-user",
											username: "user",
											image: null,
											isVerified: true,
											isFounder: false,
										},
								  ]
								: (old.data.usersWhoVoted || []).filter((user: any) => user.id !== "temp-user"),
					},
				};
			});

			return { previousData };
		},
		onError: (error: Error, _variables, context) => {
			queryClient.setQueryData(keyToInvalidate, context?.previousData);
			toast.error(error.message || "Failed to vote");
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: keyToInvalidate });
		},
	});

	const formatNumber = (num: number) => {
		if (num >= 1000) {
			return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
		}
		return `${num}`;
	};

	const handleUpVoteClick = () => {
		if (!isLoggedIn) {
			openModal(ModalType.Auth);
			return;
		}
		const type = isUpvotedPreviously ? "downvote" : "upvote";
		window.rudderanalytics?.track(type === "upvote" ? "Upvoted" : "Downvoted", {
			productId: id,
			productName: name,
			slug,
		});
		window.rudderanalytics?.track("Voted", {
			type: type === "upvote" ? "Upvote" : "Downvote",
			productName: name,
			productId: id,
			slug,
		});
		productsMutation.mutate({ id, type });
	};

	return (
		<div
			className={cn(
				"h-26 flex w-40 items-center justify-between gap-3 rounded-lg px-[6px] py-[8px] text-white",
				isPremium
					? "bg-gradient-to-r from-[#A97E2F] via-[#E0C265] to-[#C89440] text-gray-900"
					: isUpvotedPreviously
					? "bg-[#2563EB]"
					: "bg-black"
			)}
		>
			<div className="flex w-full flex-col items-center justify-center">
				<span className="text-2xl font-bold">{upVotes}</span>
				<div className="mt-1 flex w-full flex-col items-center justify-center text-center text-sm leading-tight">
					<div className="flex items-center justify-center gap-0.5">
						{[...Array(5)].map((_, i) => (
							<Star
								key={i}
								className={cn(
									"h-3 w-3 sm:h-3 sm:w-3",
									i < Math.floor(parseFloat(rating || "0"))
										? "fill-yellow-400 text-yellow-400"
										: "fill-gray-300 text-gray-300"
								)}
							/>
						))}
					</div>
					<span className="mt-1 text-xs text-white/80">{`(${formatNumber(reviewsCount)})`}</span>
				</div>
			</div>
			<Button
				onClick={(e) => {
					handleUpVoteClick();
					e.stopPropagation();
				}}
				className={cn(
					"relative flex h-16 w-[5rem] flex-col items-center justify-center gap-1 overflow-hidden rounded-md transition",
					isUpvotedPreviously
						? "border-[2px] border-white/20 bg-[#2563EB] hover:bg-[#2563EB]"
						: "bg-white hover:bg-white"
				)}
			>
				<div className="relative flex h-16 w-[4.5rem] items-center justify-center">
					<Image
						src="/images/upvote.png"
						width={28}
						height={28}
						alt="Upvote"
						className={cn(
							"absolute top-0 transition-all duration-300 ease-in-out",
							isUpvotedPreviously
								? "translate-y-[-10px] scale-90 opacity-0"
								: "translate-y-0 scale-100 opacity-100"
						)}
					/>
					<Image
						src="/images/upvoted.png"
						width={28}
						height={28}
						alt="Upvoted"
						className={cn(
							"absolute top-0 transition-all duration-300 ease-in-out",
							isUpvotedPreviously
								? "translate-y-0 scale-100 opacity-100"
								: "translate-y-[10px] scale-90 opacity-0"
						)}
					/>
				</div>

				<span
					className={cn(
						"px-2 text-[12px] font-semibold text-black",
						isUpvotedPreviously ? "text-white" : "text-black"
					)}
				>
					{isUpvotedPreviously ? "Upvoted" : "Upvote"}
				</span>
			</Button>
		</div>
	);
};

export default UpVoteButton;
